import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

import Section from "../layout/Section";
import Container from "../layout/Container";

import {
  Home,
  Settings,
  TrendingUp,
  MessageCircle,
  Palette,
  BarChart3,
} from "lucide-react";

import FeatureVisualPricing from "../features/FeatureVisualPricing";
import FeatureVisualOperations from "../features/FeatureVisualOperations";
import FeatureVisualListings from "../features/FeatureVisualListings";
import FeatureVisualGuest from "../features/FeatureVisualGuest";
import FeatureVisualReporting from "../features/FeatureVisualReporting";
import FeatureVisualDesign from "../features/FeatureVisualDesign";

import { fadeUp, stagger } from "@/utils/motion";

export default function Features() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);
  const resumeTimerRef = useRef(null);

  const features = [
    {
      title: "Listings that rank higher and convert better",
      desc: "Optimised listings with strong visuals and copy to increase visibility and bookings.",
      visual: FeatureVisualListings,
      icon: Home,
    },
    {
      title: "Complete operations handled end to end",
      desc: "Cleaning, check ins and maintenance fully managed so you stay hands off.",
      visual: FeatureVisualOperations,
      icon: Settings,
    },
    {
      title: "Pricing driven by demand not guesswork",
      desc: "Dynamic pricing based on market trends to maximise occupancy and revenue.",
      visual: FeatureVisualPricing,
      icon: TrendingUp,
    },
    {
      title: "Guest communication that improves ratings",
      desc: "Fast and consistent responses that lead to better reviews and repeat bookings.",
      visual: FeatureVisualGuest,
      icon: MessageCircle,
    },
    {
      title: "Design upgrades that increase nightly value",
      desc: "Interior improvements that enhance experience and justify higher pricing.",
      visual: FeatureVisualDesign,
      icon: Palette,
    },
    {
      title: "Clear reporting with real performance insights",
      desc: "Monthly reports covering revenue, occupancy and overall property performance.",
      visual: FeatureVisualReporting,
      icon: BarChart3,
    },
  ];

  const ActiveVisual = features[active].visual;

  useEffect(() => {
    if (paused) return;

    intervalRef.current = setInterval(() => {
      setActive((prev) => (prev + 1) % features.length);
    }, 6000);

    return () => clearInterval(intervalRef.current);
  }, [paused, features.length]);

  useEffect(() => {
    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(resumeTimerRef.current);
    };
  }, []);

  const handleUserSelect = (index) => {
    clearInterval(intervalRef.current);
    clearTimeout(resumeTimerRef.current);

    setActive(index);
    setPaused(true);

    resumeTimerRef.current = setTimeout(() => {
      setPaused(false);
    }, 6000);
  };

  return (
    <Section
      id="features"
      background="bg-bg-light"
      padding="none"
      className="pt-10 pb-16 md:pt-12 md:pb-20 lg:pt-14 lg:pb-24"
    >
      <Container>

        {/* HEADER */}
        <motion.div
          variants={stagger(0.15)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.35 }}
          className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-end"
        >
          <div className="max-w-content">
            <motion.span
              variants={fadeUp}
              className="text-small font-semibold uppercase tracking-wide text-brand-primary"
            >
              Why Zenia
            </motion.span>

            <motion.h2
              variants={fadeUp}
              className="mt-3 text-display font-extrabold text-text-primary leading-[1.05]"
            >
              Built to outperform traditional Airbnb management
            </motion.h2>

            <motion.p
              variants={fadeUp}
              className="mt-3 text-body text-text-muted max-w-reading"
            >
              A complete system designed to increase revenue and simplify property ownership across global markets.
            </motion.p>
          </div>

          <motion.div variants={fadeUp} className="lg:pb-1">
            <a href="/solutions" className="btn-outline btn-md">
              Explore our solutions →
            </a>
          </motion.div>
        </motion.div>

        {/* MAIN */}
        <div className="mt-10 grid gap-8 lg:grid-cols-[1.05fr_0.95fr] items-stretch">

          {/* LEFT VISUAL */}
          <div className="relative flex h-full">
            <div className="pointer-events-none absolute inset-0 rounded-[28px] bg-gradient-to-br from-brand-primary/10 via-transparent to-transparent blur-3xl opacity-70" />

            <div className="relative z-10 w-full overflow-hidden rounded-[28px] bg-bg-surface shadow-soft">
              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, scale: 0.975 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.985 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="
                    w-full h-full
                    bg-bg-light/60
                   p-4 md:p-4
                    min-h-[320px] lg:min-h-[520px]
                  "
                >
                  {ActiveVisual && <ActiveVisual />}
                </motion.div>
              </AnimatePresence>

              <div className="border-t border-border-soft px-5 py-5 md:px-6">
                <div className="text-body font-semibold text-text-primary">
                  {features[active].title}
                </div>
                <div className="mt-1 text-small text-text-muted">
                  {features[active].desc}
                </div>
              </div>
            </div>
          </div>

          {/* RIGHT GRID */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 auto-rows-fr">
            {features.map((f, index) => {
              const Icon = f.icon;
              const isActive = index === active;

              return (
                <motion.button
                  key={f.title}
                  type="button"
                  variants={fadeUp}
                  onClick={() => handleUserSelect(index)}
                  className={`
                    group relative overflow-hidden rounded-[20px]
                    border text-left transition-all duration-300
                    min-h-[150px] sm:min-h-[150px] h-auto sm:h-auto
                    ${isActive
                      ? "border-brand-primary/20 bg-bg-surface shadow-card"
                      : "border-border-soft bg-bg-surface hover:-translate-y-1 hover:shadow-card"
                    }
                  `}
                >
                  {isActive && (
                    <motion.div
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 6, ease: "linear" }}
                      className="absolute left-0 top-0 h-[2px] bg-brand-primary"
                    />
                  )}

                 <div className="flex h-full flex-col justify-between p-4">

                    <div className="flex items-center justify-between">
                      <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-border-soft bg-bg-light">
                        <Icon size={16} className="text-brand-primary" />
                      </div>

                      <span className="h-1.5 w-1.5 rounded-full bg-brand-primary/70" />
                    </div>

                    {/* 🔥 GRADIENT TITLE */}
                    <div className="
                      text-[15px] font-semibold leading-snug
                      bg-gradient-to-r from-brand-primary to-accent-gold
                      bg-clip-text text-transparent
                    ">
                      {f.title}
                    </div>

                    <div className="text-[13px] text-text-muted leading-relaxed">
                      {f.desc}
                    </div>

                  </div>
                </motion.button>
              );
            })}
          </div>

        </div>
      </Container>
    </Section>
  );
}